import { useState } from "react";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import type { IneligibleUser, IneligibleUsersResponse } from "@/api/assignments";
import S from "./IneligibleUsersNotice.module.scss";

interface IneligibleUsersNoticeProps {
  data: IneligibleUsersResponse;
  className?: string;
  onRemoveUsers?: () => void;
}

const displayName = (user: IneligibleUser): string =>
  [user.firstName, user.lastName].filter(Boolean).join(" ") ||
  user.email ||
  user.directoryUuid ||
  "Unknown user";

const IneligibleUsersNotice = ({
  data,
  className = "",
  onRemoveUsers,
}: IneligibleUsersNoticeProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const users = data.ineligibleUsers ?? [];
  if (users.length === 0) return null;

  const count = users.length;
  const noun = count === 1 ? "user" : "users";
  const requiredLevel = data.requiredLevel;

  return (
    <Message
      severity="warn"
      className={`w-full justify-start ${className}`}
      content={
        <div className="w-full">
          <div className="flex gap-2">
            <i className="pi pi-exclamation-triangle mt-1" />
            <div>
              <span className="font-semibold">
                {count} {noun}
              </span>{" "}
              can&apos;t start this assignment yet
              {requiredLevel ? (
                <>
                  {" "}
                  — the path is in the <span className="font-semibold">{requiredLevel}</span> level
                </>
              ) : null}
              . They will still be assigned, but must complete a path at the{" "}
              <span className="font-semibold">
                {data.prerequisiteLevel ?? requiredLevel ?? "required"}
              </span>{" "}
              level before they can enroll.
              <div className="flex gap-4 items-center mt-2">
                <Button
                  severity="secondary"
                  className={S.noticeButton}
                  label={isExpanded ? `Hide ${noun}` : `Show ${noun}`}
                  onClick={() => setIsExpanded(!isExpanded)}
                />
                {onRemoveUsers && (
                  <Button
                    severity="secondary"
                    className={S.noticeButton}
                    label={count === 1 ? "Remove this user" : `Remove these ${count} users`}
                    onClick={onRemoveUsers}
                  />
                )}
              </div>
              {isExpanded && (
                <ul className="list-disc pl-6 mt-1 mb-0 space-y-1">
                  {users.map(user => (
                    <li key={user.directoryUuid ?? user.email}>
                      {displayName(user)}
                      {user.email && user.firstName && (
                        <span className="opacity-75"> ({user.email})</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      }
    />
  );
};

export default IneligibleUsersNotice;
